import { Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, ChangeDetectionStrategy, Injector, HostBinding } from '@angular/core'
import { Subscription } from 'rxjs';

import { MatTabGroup } from '@angular/material/tabs';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { UploadingEventArgs } from '@syncfusion/ej2-angular-inputs';
import { ClickEventArgs, Toolbar } from '@syncfusion/ej2-angular-navigations';
import { EmitType } from '@syncfusion/ej2-base';

import { PdfViewerComponent } from 'ng2-pdf-viewer';

import { uiCmdComponentMaximize } from '@ui/cer-ui-commands';
import { UiCommand, UiKeyboardShortcut } from '@ui/cer-ui-types';

import { CerFormService } from '@form/cer-form.service';
import { uiCmdPaneRightClose } from '@form/cer-form-panes/cer-form-panes.service';

import { LinkedAttachmentDto as Attachment, ChatDto } from "../../app/api";
import { CerAppChatComponent } from '../cer-app-chat/cer-app-chat.component';

export const fileViewerCmdZoomIn: UiCommand = { id: 'fileViewerCmdZoomIn', text: 'Reducer detaljer' };
export const fileViewerCmdZoomOut: UiCommand = { id: 'fileViewerCmdZoomOut', text: 'Udvid detaljer' };

export const fileViewerShortcutZoomIn: UiKeyboardShortcut = { cmd: fileViewerCmdZoomIn, code: 'NumpadAdd', alt: true, ctrl: false, shift: false, enabled: true }; // Alt +
export const fileViewerShortcutZoomOut: UiKeyboardShortcut = { cmd: fileViewerCmdZoomOut, code: 'NumpadSubtract', alt: true, ctrl: false, shift: false, enabled: true }; // Alt -

export type ViewCommandEvent = 'close' | 'add';
export type TabCheckBoxMode = 'none' | 'split-select' | 'split-apply' | 'transfer-select' | 'transfer-copy' | 'transfer-paste';

export class TabSelectedApplyData {
  public mode: TabCheckBoxMode;
  public refRowIdFrom?: number;
  public refRowIdTo?: number;
  public fileNames?: string[];
}

@Component({
  selector: 'cer-app-file-viewer-tab',
  templateUrl: './cer-app-file-viewer-tab.component.html',
  styleUrls: ['./cer-app-file-viewer-tab.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class CerAppFileViewerTabComponent {
  @Input() attachments: Attachment[];
  @Input() enableChat: boolean;
  @Input() enableClose: boolean = true;
  @Input() allowTransfer: boolean = false;
  @Input() chats: ChatDto[];
  @Input() refTableId: number;
  @Input() refRowId: number;
  @Input() token: string;
  @Input() saveUrl: string;
  @Input() removeUrl: string;
  @Input() resized: boolean;
  @Input() tabCheckBoxAllowed: boolean = false;
  @Input() tabSelectedIndexInitial: number;
  @Input() uploadIsActive: boolean = false;
  @Input() formServiceActive: boolean = true;
  @Input() privateNoteActive: boolean = false;

  @Output() viewCommand: EventEmitter<ViewCommandEvent> = new EventEmitter<ViewCommandEvent>();
  @Output() tabCheckBoxApply: EventEmitter<TabSelectedApplyData> = new EventEmitter<TabSelectedApplyData>();
  @Output() tabSelectedIndexChanged: EventEmitter<number> = new EventEmitter<number>();

  // Component
  @HostBinding("class.maximized")
  protected isMaximized: boolean = false;
  @HostBinding("class.default")
  protected hostClassDefault = true;

  private subscriptionManager$: Subscription = new Subscription();

  constructor(private injector: Injector) {
  }

  // Contenttype
  private contentTypesBody: string[] = ['text/html', 'text/plain']
  private textExtensions: string[] = ['.json', '.xml']
  private contentTypesImage: string[] = ['image/jpeg', 'image/png', 'image/gif']
  private contentTypesPdf: string[] = ['application/pdf']

  //
  public tabCheckBoxModePossible: boolean = false;

  boolean = false;
  public tabChecked: boolean[] = null;
  private tabCheckBoxData: TabSelectedApplyData = { mode: 'none' };
  public uploadDropAreaElement: HTMLElement;
  public uploadPath: Object;
  public zoomStyle: string = '';
  private zoomFactor: number = 1.0;

  public refRowIdCurr: number = null;



  // Toolbar
  protected attachmentUrlOriginal: string;
  protected attachmentUrlOriginalClass: string;
  protected attachmentUrlDisplay: string;
  protected attachmentUrlDisplayClass: string;
  private attachmentCanZoom: boolean = false;
  protected uiCmdComponentMaximize = uiCmdComponentMaximize;
  protected uiCmdPaneRightClose = uiCmdPaneRightClose;

  private toolbarInstance: Toolbar;
  @ViewChild('toolbar', { static: false }) set toolbarContent(toolbarInstance: Toolbar) {
    if (toolbarInstance && !this.toolbarInstance) {
      this.toolbarInstance = toolbarInstance;
      this.tabSelectedInit(this.tabSelectedIndexInitial ?? 0);
    }
  }

  // Tabs
  private tabGroup: MatTabGroup;
  protected tabSelectedIndex: number = 0;
  @ViewChild('tabGroup', { static: false }) set tabGroupContent(tabGroup: MatTabGroup) {
    if (tabGroup && !this.tabGroup) {
      this.tabGroup = tabGroup;
    }
  }

  private pdfViewer: PdfViewerComponent;
  @ViewChild('pdfViewer', { static: false }) set pdfViewerContent(pdfViewer: PdfViewerComponent) {
    if (pdfViewer) {
      this.pdfViewer = pdfViewer;
    }
  }

  @ViewChild(CerAppChatComponent) chatViewer: CerAppChatComponent;


  ngOnInit() {

    this.uploadDropAreaElement = document.getElementById('uploadDropArea');
    this.tabSelectedIndexChanged.subscribe(idx => this.onTabSelected(idx));
    if (this.formServiceActive) {
      var formService: CerFormService = <CerFormService>this.injector.get(CerFormService);
      //this.subscriptionManager$.add(formService.formCommand$.subscribe(formCommandArgs => this.onFormCommand(formCommandArgs)));
      this.subscriptionManager$.add(formService.parentData$.subscribe(parentDataArgs => this.onParentData(parentDataArgs)));
    }
  }

  ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['attachments'] || changes['refRowId'] || changes['tabSelectedIndexInitial']) {
      this.onAttachmentsChanged();
      this.uploadPath = {
        saveUrl: this.saveUrl,
        removeUrl: this.removeUrl
      };
      var tabIndex: number = this.tabSelectedIndexInitial ?? this.tabSelectedIndex ?? 0;
      if (this.tabSelectedIndex != tabIndex) {
        this.tabSelectedIndex = tabIndex;
      }
      else {
        this.onTabSelected(tabIndex, true);
      }
    }
  }

  private onParentData(parentDataArgs: any) {
    if (!parentDataArgs.row && parentDataArgs.stable) {
      // Force reset (to-do: make this the primary input parameter event)
      /*
      this.token = null;
      this.refRowId = null;
      this.refTableId = null;
      this.attachments = [];
      this.chats = [];
      */
    }
  }

  // Tabs
  protected onTabSelectedEvent(index: number) {
    this.onTabSelected(index, true);
  }

  private tabSelectedIndexCurr: number = null;
  protected onTabSelected(idx: number, force: boolean = false) {
    if ((this.tabSelectedIndexCurr != idx && idx >= 0) || force) {
      this.tabSelectedIndexCurr = idx;
      this.tabSelectedInit(idx);
      this.tabSelectedIndexChanged.emit(idx);
    }
    /*
    if (this.tabGroup) {
      var tabGroupElement: any = (this.tabGroup as any)?._closestTabGroup;
      if (tabGroupElement && tabGroupElement.selectedIndex != idx) {
        this.tabGroup.selectedIndex = idx;
        tabGroupElement.selectedIndex = idx;
      }
    }
    */
  }

  private tabSelectedInit(idx: number) {
    this.setAttachmentOpenUrl(idx);
    this.selectTabInitChat(idx);
    this.toolbarRefresh();
  }

  private tabChatSelected: boolean = false;
  private selectTabInitChat(idx: number) {
    if (!this.enableChat) {
      this.tabChatSelected = false;
    }
    else {
      var tabGroup: any = this.tabGroup;
      var tabLen: number = tabGroup?._closestTabGroup?._allTabs?.length ?? 0;
      this.tabChatSelected = tabLen > 0 && idx === tabLen - 1;
      if (this.tabChatSelected) {
        this.chatViewerSetFocus();
      }
      if (this.chatViewer && this.tabChatSelected) {
        this.chatViewer.checkMarkRead();
        this.chatViewerSetFocus();
      }
    }
  }

  private setAttachmentOpenUrl(idx: number) {
    var attachment: Attachment = null;
    if (this.attachments && this.attachments.length > idx) {
      attachment = this.attachments[idx];
    }

    const hasUrlDisplay =  attachment?.originalUrl?.length > 0;
    this.attachmentUrlOriginal = hasUrlDisplay ? attachment?.originalUrl : attachment?.url;
    this.attachmentUrlOriginalClass = this.attachmentUrlOriginal?.length > 0 ? "" : "display-none";
    this.attachmentUrlDisplay = hasUrlDisplay ? attachment?.url : null;
    this.attachmentUrlDisplayClass = this.attachmentUrlDisplay?.length > 0 ? "" : "display-none";
    this.attachmentCanZoom = this.contentIsPdf(attachment) || this.contentIsImage(attachment) || this.contentIsBody(attachment);
    this.zoomStyle = '';
    this.zoomFactor = 1.0;
    this.toolbarRefresh();
  }

  private tabCheckedDefaultCount: number = 0;
  private onAttachmentsChanged() {
    var maxIdx: number = 0;
    if (this.attachments) {
      this.attachments.forEach(a => maxIdx = Math.max(maxIdx, a.attachmentIndex));
      this.tabChecked = new Array(maxIdx);
      var i: number = 0;
      this.attachments.forEach(a => {
        var defaultChecked: boolean = this.contentTypeTabSelectedDefault(a);
        if (defaultChecked) {
          i++;
        }
        this.tabChecked[a.attachmentIndex] = defaultChecked;
      });
      this.tabCheckedDefaultCount = i;
    }

    var checkPossible = (this.tabCheckBoxAllowed && this.attachments && this.attachments.length > 0);
    if (checkPossible != this.tabCheckBoxModePossible) {
      this.tabCheckBoxModePossible = checkPossible;
    }

    if (this.refRowId != this.refRowIdCurr) {
      this.refRowIdCurr = this.refRowId;
      if (this.tabCheckBoxData.mode != 'transfer-copy') {
        this.tabCheckBoxDataSet('none');
      }
    }
  }

  public toolbarRefresh() {

    if (this.toolbarInstance) {
      var t: Toolbar = this.toolbarInstance;
      if ((<any>t).tbarEle) {
        var docs: boolean = this.attachments?.length > 0 && !this.tabChatSelected;
        var docsCheck: boolean = docs && this.tabCheckBoxModePossible;
        var docsMulti: boolean = docsCheck && this.attachments?.length > 2;
        var transfer: boolean = this.allowTransfer;
        var tabMode: TabCheckBoxMode = this.tabCheckBoxData.mode;
        var checkCount: number = this.tabChecked?.filter(t => t).length ?? 0;
        t.hideItem(0, !(this.enableClose));  // Close
        t.hideItem(1, !(this.saveUrl?.length > 0 && this.token != null && this.refRowId != null)); // Upload
        t.hideItem(2, !(docs && this.attachmentUrlOriginal?.length > 0));
        t.hideItem(3, !(docs && this.attachmentUrlDisplay?.length > 0));
        t.hideItem(4, !(docs && this.attachmentCanZoom));
        t.hideItem(5, !(docs && this.attachmentCanZoom));
        t.hideItem(6, !(docsMulti && tabMode == 'none')); // Show split checkbox
        t.hideItem(7, !(docsMulti && tabMode == 'split-select' && checkCount > 1));  // Apply split
        t.hideItem(8, !(transfer && docsCheck && tabMode == 'none')); // Show transfer checkbox
        t.hideItem(9, !(transfer && docsCheck && tabMode == 'transfer-select' && checkCount > 0));  // Save for transfer
        t.hideItem(10, !(transfer && tabMode == 'transfer-copy' && this.refRowId != this.tabCheckBoxData.refRowIdFrom));  // Transfer paste
        t.hideItem(11, !(tabMode != 'none')); // Cancel checkbox
        //t.hideItem(12, !(this.enableMaximize));  // maximize
      }
    }
  }

  public resize() {
    setTimeout(() => {
      this.toolbarRefresh();
      if (this.attachments && this.contentIsPdf(this.attachments[this.tabSelectedIndex])) {
        if (this.pdfViewer) {
          this.pdfViewer.zoom = 1.0;
          this.pdfViewer.afterLoadComplete
          this.pdfUpdateSize();
        }
      }
      else {
        this.zoomFactor = 1.0;
        this.zoomStyle = '';
      }
    });
  }

  private zoomIn() {
    if (this.contentIsPdf(this.attachments[this.tabSelectedIndex])) {
      this.pdfZoomIn();
    }
    else {
      this.zoomFactor += 0.1;
      this.zoomStyleCalc();
    }
  }

  private zoomOut() {
    if (this.contentIsPdf(this.attachments[this.tabSelectedIndex])) {
      this.pdfZoomOut();
    }
    else {
      this.zoomFactor -= 0.1;
      if (this.zoomFactor < 0.1) {
        this.zoomFactor = 0.1;
      }
      this.zoomStyleCalc();
    }
  }

  private zoomStyleCalc() {
    if (this.zoomFactor == 1.0) {
      this.zoomStyle = '';
    }
    else {
      this.zoomStyle = 'transform: scale(' + this.zoomFactor + '); transform-origin: 0% 0%;';
    }
  }

  contentIsBody(attachment: Attachment): boolean {
    var isBody: boolean = false

    if (attachment != null && attachment.fileName.startsWith('body') && this.contentTypesBody.includes(attachment.contentType)) {
      isBody = true
    }

    return isBody;
  }

  private contentTypeTabSelectedDefault(attachment: Attachment): boolean {
    return !this.contentIsBody(attachment) &&
      !this.contentIsImage(attachment) &&
      !this.contentIsText(attachment);
  }

  contentIsText(attachment: Attachment): boolean {
    var isText: boolean = false

    if (attachment != null && this.textExtensions.includes(attachment.fileExtension)) {
      isText = true
    }

    return isText;
  }

  plainUrl(attachment: Attachment): string {
    return attachment.url + "&plain=1";
  }

  contentIsPdf(attachment: Attachment): boolean {
    return attachment != null && this.contentTypesPdf.includes(attachment.contentType)
  }

  contentIsImage(attachment: Attachment): boolean {
    return attachment != null && this.contentTypesImage.includes(attachment.contentType)
  }

  contentIsLink(attachment: Attachment): boolean {
    return attachment != null && !(this.contentIsBody(attachment) ||
      this.contentIsText(attachment) ||
      this.contentIsPdf(attachment) ||
      this.contentIsImage(attachment))
  }

  public toolbarClick(args: ClickEventArgs) {
    //console.log("Toolbar id: " + args.item.id);
    switch (args.item.id) {
      case 'uploadShowHide':
        this.uploadShowHide();
        break;
      case 'openAttachmentInNewTab':
        this.openAttachInNewTab(true);
        break;
      case 'openAttachmentOriginalInNewTab':
        this.openAttachInNewTab(false);
        break;
      case 'zoomIn':
        this.zoomIn();
        break;
      case 'zoomOut':
        this.zoomOut();
        break;
      case 'tabCheckboxSplitSelect':
        this.tabCheckBoxDataSet('split-select');
        break;
      case 'tabCheckboxSplitApply':
        this.tabCheckBoxDataSet('split-apply');
        break;
      case 'tabCheckboxTransferSelect':
        this.tabCheckBoxDataSet('transfer-select');
        break;
      case 'tabCheckboxTransferCopy':
        this.tabCheckBoxDataSet('transfer-copy');
        break;
      case 'tabCheckboxTransferPaste':
        this.tabCheckBoxDataSet('transfer-paste');
        break;
      case 'tabCheckboxCancel':
        this.tabCheckBoxDataSet('none');
        break;
      case 'close':
        this.isMaximized = false;
        this.close();
        break;
      case 'maximize':
        this.isMaximized = !this.isMaximized;
        this.resize();
        break;
    }
  }

  public toolbarCreated(args: any) {
    this.toolbarRefresh();
  }

  public tabCheckboxSet(attachment: Attachment, event: MatCheckboxChange) {
    this.tabChecked[attachment.attachmentIndex] = event.checked;
    this.toolbarRefresh()
  }

  protected tabCheckBoxMode: TabCheckBoxMode = 'none';
  public tabCheckBoxDataSet(mode: TabCheckBoxMode) {
    this.tabCheckBoxData.mode = mode;
    switch (mode) {
      case 'none':
        this.tabCheckBoxData = { mode: 'none' };
        break;
      case 'split-apply':
        this.tabCheckBoxData.refRowIdFrom = this.refRowId;
        this.tabCheckBoxData.fileNames = this.selectedTabFileNames();
        this.tabCheckBoxApply.emit(this.tabCheckBoxData);
        break;
      case 'transfer-copy':
        this.tabCheckBoxData.refRowIdFrom = this.refRowId;
        this.tabCheckBoxData.fileNames = this.selectedTabFileNames();
        break;
      case 'transfer-paste':
        this.tabCheckBoxData.refRowIdTo = this.refRowId;
        this.tabCheckBoxApply.emit(this.tabCheckBoxData);
    }
    this.tabCheckBoxMode = mode;
    this.toolbarRefresh();
  }

  private selectedTabFileNames(): string[] {
    var fileNames: string[] =
      this.attachments.filter(a => this.tabChecked[a.attachmentIndex]).map(a => a.fileName + a.fileExtension);
    return fileNames;
  }

  public uploadShowHide() {
    this.uploadIsActive = (!this.uploadIsActive && this.token != null && this.refRowId != null);
  }

  // Open file in new tab
  public openAttachInNewTab(original: boolean) {
    if (this.tabGroup != null) {
      var idx: number = this.tabSelectedIndex;
      if (this.attachments != null && this.attachments.length > idx) {
        var attachment: Attachment = this.attachments[idx];
        if (attachment) {
          var url: string = original ? attachment.originalUrl : attachment.url;
          if (url?.length > 0) {
            window.open(url, '_blank');
          }
        }
      }
    }
  }


  private chatViewerSetFocus() {
    if (this.chatViewer) {
      this.chatViewer.refresh();

      if (this.tabsHasFocus()) {
        this.chatViewer.setFocus();
        this.chatViewer.scrollToBottom();
      }
    }
  }

  private tabsHasFocus(): boolean {
    var tabGroupElement: any = (this.tabGroup as any)?._closestTabGroup;
    var focusElement: Element = document.activeElement;
    if (tabGroupElement && focusElement) {
      return (focusElement == tabGroupElement || tabGroupElement?._elementRef?.nativeElement?.contains(focusElement));
    }
    return false;
  }

  // Chat
  public chatOpen(idx: number = this.attachments?.length ?? 0) {
    this.tabSelectedIndex = idx;
    this.onTabSelected(idx, true);
  }

  // Zoom
  public pdfUpdateSize() {
    var _pdfViewer: any = <any>this.pdfViewer;
    if (_pdfViewer?._pdf) {
      this.pdfViewer.updateSize();
    }
  }

  public pdfZoomIn() {
    if (this.pdfViewer) {
      if (!this.pdfViewer.zoom) {
        this.pdfViewer.zoom = 1.25;
      }
      else {
        this.pdfViewer.zoom = this.pdfViewer.zoom + 0.25;
      }
      this.pdfUpdateSize();
    }
  }

  public pdfZoomOut() {
    if (this.pdfViewer) {
      if (!this.pdfViewer.zoom) {
        this.pdfViewer.zoom = 0.75;
      }
      else if (this.pdfViewer.zoom > 0.25) {
        this.pdfViewer.zoom = this.pdfViewer.zoom - 0.25;
      }
      this.pdfUpdateSize();
    }
  }

  public nextTab() {
    if (this.tabSelectedIndex + 1 < this.attachments.length) {
      this.tabSelectedIndex++;
    }
  }

  public prevTab() {
    if (this.tabSelectedIndex > 0) {
      this.tabSelectedIndex--;
    }
  }

  public close() {
    this.viewCommand.emit('close');
  }

  // Uploader
  public onUploadSuccess(args: any): void {
    //    console.log(args.operation);

    if (args.operation === 'upload') {
      console.log('File uploaded successfully');
      this.viewCommand.emit("add");
    }
  }

  public onUploading: EmitType<UploadingEventArgs> = (args: any) => {
    args.customFormData = [{ 'id': this.refRowId }, { 'linktype': this.refTableId }, { 'token': this.token }];
  }

  // Chat
  public refreshChat() {
    if (this.enableChat && this.chatViewer) {
      this.chatViewer.refresh();
    }
  }
}
